"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";
import ServiceQuickNav from "@/components/ServiceQuickNav";

const cloudPlatforms = [
  {
    icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 19h11a4.5 4.5 0 0 0 .5-8.97A7 7 0 0 0 4.06 13.5 3.5 3.5 0 0 0 6.5 19z"/><path d="M12 10v5M9.5 12.5h5"/></svg>),
    name: "Microsoft Azure",
    tagline: "Enterprise Cloud Infrastructure",
    desc: "Virtual machines, Azure Virtual Desktop, hybrid cloud, backup, security, and identity management — all on Microsoft's enterprise cloud platform.",
    color: "#0089D6",
    glowColor: "rgba(0,137,214,0.15)",
    borderColor: "rgba(0,137,214,0.25)",
    href: "/cloud-solutions/azure",
    badges: ["Azure Certified", "Microsoft Partner", "Hybrid Cloud"],
    cta: "Explore Azure Services",
  },
  {
    icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 19h11a4.5 4.5 0 0 0 .5-8.97A7 7 0 0 0 4.06 13.5 3.5 3.5 0 0 0 6.5 19z"/><path d="M9.5 13l2-2 2 2" strokeWidth="1.8"/><line x1="11.5" y1="11" x2="11.5" y2="16"/></svg>),
    name: "Amazon AWS",
    tagline: "Scalable Cloud Platform",
    desc: "EC2, S3, RDS, VPC, and managed AWS environments — Pro IT Solutions designs and manages AWS infrastructure for New Zealand businesses.",
    color: "#FF9900",
    glowColor: "rgba(255,153,0,0.12)",
    borderColor: "rgba(255,153,0,0.25)",
    href: "/cloud-solutions/aws",
    badges: ["AWS Partner", "Managed Cloud", "Migration Specialists"],
    cta: "Explore AWS Services",
  },
  {
    icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="12" y1="3" x2="12" y2="21"/></svg>),
    name: "Microsoft 365",
    tagline: "Modern Workplace Platform",
    desc: "Teams, Exchange, SharePoint, OneDrive, Intune, and full M365 security — complete Microsoft 365 setup, migration, and ongoing management.",
    color: "#0078D4",
    glowColor: "rgba(0,120,212,0.12)",
    borderColor: "rgba(0,120,212,0.25)",
    href: "/cloud-solutions/microsoft-365",
    badges: ["M365 Certified", "Microsoft Partner", "Modern Workplace"],
    cta: "Explore M365 Services",
  },
];

const migrationSteps = [
  { num: "01", title: "Assessment", desc: "Evaluate your current environment, workloads, and business requirements to identify the right cloud approach." },
  { num: "02", title: "Design", desc: "Architect your cloud environment with security, scalability, and cost optimisation built in from the start." },
  { num: "03", title: "Migration", desc: "Structured migration with minimal disruption — moving workloads, data, and applications to the cloud." },
  { num: "04", title: "Testing", desc: "Thorough validation of all migrated services, performance benchmarking, and security verification." },
  { num: "05", title: "Optimisation", desc: "Fine-tune your cloud environment for performance, cost, and security after go-live." },
  { num: "06", title: "Management", desc: "Ongoing monitoring, management, and support to keep your cloud environment running at its best." },
];

const benefits = [
  { icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>), title: "Scalability", desc: "Scale resources up or down instantly to match your business demands." },
  { icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>), title: "Cost Efficiency", desc: "Pay only for what you use and eliminate costly on-premises hardware." },
  { icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>), title: "Anywhere Access", desc: "Securely access your systems and data from any device, anywhere in New Zealand and beyond." },
  { icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>), title: "Business Continuity", desc: "Built-in redundancy and disaster recovery to keep your business running 24/7." },
];

export default function CloudSolutionsPage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Cloud Solutions" },
          ]}
        />

        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 40%, #0a1628 70%, #070B14 100%)" }} />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(14,165,233,0.12) 0%, transparent 70%)" }} />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,120,212,0.08) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(14,165,233,0.12)", border: "1px solid rgba(14,165,233,0.25)" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: "#0EA5E9" }} />
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Cloud Solutions</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                Cloud Solutions Built<br />
                <span className="gradient-text">for Business</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg max-w-2xl mb-10" style={{ color: "#94A3B8" }}>
                Pro IT Solutions designs, deploys, and manages cloud environments that give New Zealand businesses the agility, security, and performance they need to grow.
              </p>
            </FadeInView>
            <FadeInView delay={0.45}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-600 rounded-pill btn-primary">Book a Free Cloud Assessment</Link>
                <Link href="/contact" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-500 rounded-pill btn-secondary">Talk to an Expert</Link>
              </div>
            </FadeInView>
          </div>
        </section>

        {/* Cloud Platforms */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Cloud Platforms We Specialise In</h2>
              <p className="text-body-lg max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>We partner with the world&apos;s leading cloud platforms to deliver the right solution for your business.</p>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch" staggerDelay={0.1}>
              {cloudPlatforms?.map((platform) => (
                <div
                  key={platform?.name}
                  className="rounded-card-lg p-5 sm:p-8 flex flex-col h-full"
                  style={{ background: platform?.glowColor, border: `1px solid ${platform?.borderColor}` }}
                >
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ background: `${platform?.color}20`, border: `1px solid ${platform?.color}40` }}>
                    <span className="flex items-center justify-center" style={{ color: platform?.color }}>{platform?.icon}</span>
                  </div>
                  <div className="text-[12px] font-body font-medium uppercase tracking-widest mb-2" style={{ color: platform?.color }}>{platform?.tagline}</div>
                  <h3 className="font-heading font-700 text-[24px] mb-3" style={{ color: "#F8FAFC" }}>{platform?.name}</h3>
                  <p className="text-[14px] font-body leading-[1.75] mb-6" style={{ color: "#94A3B8" }}>{platform?.desc}</p>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {platform?.badges?.map((badge) => (
                      <span key={badge} className="text-[11px] font-body px-3 py-1 rounded-pill" style={{ background: `${platform?.color}15`, color: platform?.color, border: `1px solid ${platform?.color}30` }}>{badge}</span>
                    ))}
                  </div>
                  <div className="mt-auto">
                    <Link
                      href={platform?.href}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-pill text-[14px] font-heading font-600 transition-all duration-300"
                      style={{ background: `${platform?.color}20`, color: platform?.color, border: `1px solid ${platform?.color}40` }}
                    >
                      {platform?.cta}
                      <span>→</span>
                    </Link>
                  </div>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 lg:py-28 border-t" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-12">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Why Move to the Cloud?</h2>
              <p className="text-body-lg max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>Cloud technology gives your business the flexibility and resilience to compete and grow in today&apos;s fast-moving environment.</p>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch" staggerDelay={0.1}>
              {benefits?.map((benefit) => (
                <div key={benefit?.title} className="rounded-card-lg p-6 text-center h-full flex flex-col" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: "rgba(59,130,246,0.12)", color: "#60A5FA" }}>{benefit?.icon}</div>
                  <h3 className="font-heading font-700 text-[24px] mb-2" style={{ color: "#F8FAFC" }}>{benefit?.title}</h3>
                  <p className="text-[14px] font-body leading-[1.75]" style={{ color: "#94A3B8" }}>{benefit?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Migration Process */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Cloud Migration Process</h2>
              <p className="text-body-lg max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>A structured, low-risk approach to moving your business to the cloud.</p>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 items-stretch" staggerDelay={0.08}>
              {migrationSteps?.map((step) => (
                <div key={step?.num} className="rounded-card-lg p-5 text-center h-full flex flex-col" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="font-heading font-800 text-[32px] mb-2" style={{ color: "rgba(14,165,233,0.3)" }}>{step?.num}</div>
                  <h3 className="font-heading font-600 text-[14px] mb-2" style={{ color: "#F8FAFC" }}>{step?.title}</h3>
                  <p className="text-[12px] font-body" style={{ color: "#94A3B8" }}>{step?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Not Sure Which Platform Advisory */}
        <section className="py-20 lg:py-28 border-t" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.97}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-14" style={{ background: "rgba(14,165,233,0.06)", border: "1px solid rgba(14,165,233,0.18)" }}>
                <div className="flex flex-col lg:flex-row items-center gap-10">
                  <div className="flex-1">
                    <div className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text mb-3">Cloud Advisory</div>
                    <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Not sure which cloud platform is right for you?</h2>
                    <p className="text-[15px] font-body leading-[1.75] mb-6" style={{ color: "#94A3B8" }}>
                      Every business has different requirements. Our cloud specialists will assess your current environment, workloads, and goals — then recommend the right platform and migration approach for your business.
                    </p>
                    <ul className="space-y-3 mb-8">
                      {["Free cloud readiness assessment", "Platform comparison and recommendation", "Migration planning and cost modelling", "Ongoing managed cloud support"]?.map((item) => (
                        <li key={item} className="flex items-center gap-3 text-[14px] font-body" style={{ color: "#CBD5E1" }}>
                          <span className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] flex-shrink-0" style={{ background: "rgba(14,165,233,0.2)", color: "#38BDF8" }}>✓</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                    <Link href="/contact" className="inline-flex items-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600 btn-primary">Get a Free Cloud Assessment</Link>
                  </div>
                  <div className="flex-shrink-0 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {cloudPlatforms?.map((p) => (
                      <Link
                        key={p?.name}
                        href={p?.href}
                        className="flex flex-col items-center gap-2 p-4 rounded-2xl transition-all duration-300 hover:scale-105"
                        style={{ background: p?.glowColor, border: `1px solid ${p?.borderColor}` }}
                      >
                        <span className="flex items-center justify-center" style={{ color: p?.color }}>{p?.icon}</span>
                        <span className="text-[11px] font-body text-center" style={{ color: "#94A3B8" }}>{p?.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(14,165,233,0.08)", border: "1px solid rgba(14,165,233,0.2)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Ready to Move to the Cloud?</h2>
                <p className="text-body-lg mb-8" style={{ color: "#94A3B8" }}>Book a free cloud assessment and let our experts design the right cloud solution for your business.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600 btn-primary">Book a Free Cloud Assessment</Link>
                  <Link href="/contact" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-500 btn-secondary">Talk to an Expert</Link>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        <ServiceQuickNav currentPath="/cloud-solutions" />

        <Footer />
      </main>
    </PageTransition>
  );
}
